import { FC, useState } from 'react';
import { NavLink } from 'react-router';
import { Logo } from '@/components/Logo';
import { Role } from '@/models/auth';
import { Balance } from './Balance';
import { routes } from '@/features/navigation/constants';
import { SidebarButton } from '@/features/sidebar/SidebarButton';
import { cx } from '@emotion/css';
import { Trans, useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

export const Sidebar: FC<{
  role: Role;
}> = ({ role }) => {
  const { i18n } = useTranslation();
  const [showNavLabels, setShowNavLabels] = useState(true);

  const toggleIcon = showNavLabels ? (
    <ChevronLeftIcon fontSize="medium" />
  ) : (
    <ChevronRightIcon fontSize="medium" />
  );

  return (
    <nav
      className={cx(
        'h-full px-4 py-4 transition-all relative flex flex-col',
        showNavLabels ? 'w-60' : 'w-16 items-center'
      )}
    >
      <IconButton
        sx={{
          position: 'absolute',
          top: '10px',
          right: 0,
          transform: 'translateX(50%)',
          zIndex: 30,
          padding: '0',
          transition: 'left 0s',
          '&:hover': { backgroundColor: 'grey.200' },
          border: '1px solid',
          borderColor: 'grey.100',
          backgroundColor: 'background.paper',
        }}
        onClick={() => setShowNavLabels(!showNavLabels)}
      >
        {toggleIcon}
      </IconButton>

      <div className="flex justify-center pb-2 mb-6">
        <Logo size={showNavLabels ? 'large' : 'small'} />
      </div>

      {role === Role.AdPartner && (
        <NavLink to="/client/wallet">
          <div
            className={cx(
              'flex justify-center mb-6',
              showNavLabels ? 'w-full' : 'w-fit'
            )}
          >
            <Balance showDetails={showNavLabels} />
          </div>
        </NavLink>
      )}
      <ul className="flex flex-col">
        {routes[role].map(({ to, Icon, labelKey }) => (
          <NavLink key={`${to}-${i18n.language}`} to={to} end className="mb-1">
            {({ isActive }) => (
              <SidebarButton
                active={isActive}
                fullWidth
                sx={{
                  display: 'flex',
                  gap: '5px',
                  justifyContent: showNavLabels ? 'start' : 'center',
                  '&.MuiButtonBase-root': {
                    padding: showNavLabels ? '8px' : '5px',
                    minWidth: 'unset',
                    width: showNavLabels ? '100%' : '50px',
                  },
                }}
              >
                <Icon
                  style={{ fontSize: 30 }}
                  className={cx({
                    'text-gray-700': !isActive,
                    'text-primary-700': isActive,
                  })}
                />
                {showNavLabels && (
                  <h2
                    className={cx('text-base font-medium', {
                      'text-gray-700': !isActive,
                      'text-primary-700': isActive,
                    })}
                  >
                    <Trans i18nKey={labelKey} />
                  </h2>
                )}
              </SidebarButton>
            )}
          </NavLink>
        ))}
      </ul>
    </nav>
  );
};
